import { Capacitor, registerPlugin } from "@capacitor/core";

export type AccountRole = "admin" | "operator" | "user";

export interface PublicAccount {
  id: string;
  role: AccountRole;
  displayName: string | null;
}

export type SessionSnapshot =
  | { state: "signedOut"; account: null; expiresAt: null }
  | { state: "authenticated"; account: PublicAccount; expiresAt: string };

export interface LoginInput {
  email: string;
  password: string;
}

export type CertificateExportResult = { state: "presented" | "shared" | "dismissed" };

interface NativeSecureSessionPlugin {
  login(input: LoginInput): Promise<unknown>;
  status(): Promise<unknown>;
  refresh(): Promise<unknown>;
  logout(): Promise<unknown>;
  exportCertificate(input: { sessionId: string }): Promise<unknown>;
}

const nativePlugin = registerPlugin<NativeSecureSessionPlugin>("AiqsSecureSession");
const SECRET_KEY = /(token|password|secret|cookie|authorization)/i;
const SESSION_KEYS = new Set(["state", "account", "expiresAt"]);
const ACCOUNT_KEYS = new Set(["id", "role", "displayName"]);
const ROLES = new Set<AccountRole>(["admin", "operator", "user"]);
const CERTIFICATE_KEYS = new Set(["state"]);
const CERTIFICATE_STATES = new Set<CertificateExportResult["state"]>(["presented", "shared", "dismissed"]);
const CANONICAL_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

export class SecureSessionBoundaryError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "SecureSessionBoundaryError";
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function assertAllowedKeys(value: Record<string, unknown>, allowed: Set<string>, label: string): void {
  for (const key of Object.keys(value)) {
    if (SECRET_KEY.test(key)) {
      throw new SecureSessionBoundaryError(`${label} exposed a forbidden secret-like field`);
    }
    if (!allowed.has(key)) {
      throw new SecureSessionBoundaryError(`${label} exposed an unknown field`);
    }
  }
}

export function parseSessionSnapshot(value: unknown, now = Date.now()): SessionSnapshot {
  if (!isRecord(value)) throw new SecureSessionBoundaryError("session response must be an object");
  assertAllowedKeys(value, SESSION_KEYS, "session response");

  if (value.state === "signedOut" && value.account === null && value.expiresAt === null) {
    return { state: "signedOut", account: null, expiresAt: null };
  }

  if (value.state !== "authenticated" || !isRecord(value.account)) {
    throw new SecureSessionBoundaryError("session response has an invalid state");
  }
  assertAllowedKeys(value.account, ACCOUNT_KEYS, "account response");

  const { id, role, displayName } = value.account;
  if (typeof id !== "string" || id.length === 0 || !ROLES.has(role as AccountRole)) {
    throw new SecureSessionBoundaryError("account response is invalid");
  }
  if (displayName !== null && typeof displayName !== "string") {
    throw new SecureSessionBoundaryError("account display name is invalid");
  }
  if (typeof value.expiresAt !== "string") {
    throw new SecureSessionBoundaryError("session expiry is invalid");
  }
  const expiresAt = Date.parse(value.expiresAt);
  if (Number.isNaN(expiresAt) || expiresAt <= now) {
    throw new SecureSessionBoundaryError("session expiry is invalid");
  }

  return {
    state: "authenticated",
    account: { id, role: role as AccountRole, displayName },
    expiresAt: value.expiresAt,
  };
}

export function parseCertificateExportResult(value: unknown): CertificateExportResult {
  if (!isRecord(value)) throw new SecureSessionBoundaryError("certificate export response must be an object");
  assertAllowedKeys(value, CERTIFICATE_KEYS, "certificate export response");
  if (typeof value.state !== "string" || !CERTIFICATE_STATES.has(value.state as CertificateExportResult["state"])) {
    throw new SecureSessionBoundaryError("certificate export response has an invalid state");
  }
  return { state: value.state as CertificateExportResult["state"] };
}

export function validateCertificateSessionId(sessionId: string): string {
  if (!CANONICAL_UUID.test(sessionId)) {
    throw new SecureSessionBoundaryError("certificate session id is invalid");
  }
  return sessionId;
}

function assertLoginInput(input: LoginInput): LoginInput {
  const email = input.email.trim();
  if (email.length < 3 || email.length > 254 || !email.includes("@")) {
    throw new SecureSessionBoundaryError("email is invalid");
  }
  if (input.password.length < 1 || input.password.length > 1024) {
    throw new SecureSessionBoundaryError("password is invalid");
  }
  return { email, password: input.password };
}

function requireNativePlatform(): void {
  if (!Capacitor.isNativePlatform()) {
    throw new SecureSessionBoundaryError("secure session is available only in the native application");
  }
}

export const secureSession = {
  async login(input: LoginInput): Promise<SessionSnapshot> {
    requireNativePlatform();
    return parseSessionSnapshot(await nativePlugin.login(assertLoginInput(input)));
  },
  async status(): Promise<SessionSnapshot> {
    requireNativePlatform();
    return parseSessionSnapshot(await nativePlugin.status());
  },
  async refresh(): Promise<SessionSnapshot> {
    requireNativePlatform();
    return parseSessionSnapshot(await nativePlugin.refresh());
  },
  async logout(): Promise<SessionSnapshot> {
    requireNativePlatform();
    return parseSessionSnapshot(await nativePlugin.logout());
  },
  async exportCertificate(sessionId: string): Promise<CertificateExportResult> {
    requireNativePlatform();
    return parseCertificateExportResult(
      await nativePlugin.exportCertificate({ sessionId: validateCertificateSessionId(sessionId) }),
    );
  },
};
